import castArray from 'lodash/castArray';
import React, {
  ElementType,
  ReactElement,
  Suspense,
  useCallback,
  useContext,
  useState
} from 'react';
import { useAsync } from 'react-async';
import colors from '../colors.json';
import ErrorBoundary from '../components/ErrorBoundary';
import Loader from '../components/Loader';
import GlobalLoaderContext from '../contexts/global-loader';
import PageDataContext from '../contexts/page-data';
import { AnyObject, ApiResponse } from '../types';
import { useWindowHeight } from './hooks';

export const withAsyncLoader = <P extends AnyObject = AnyObject>(
  promiseFns: (() => Promise<ApiResponse>)[] = [],
  {
    shouldShowLoader,
    loader = <Loader color={colors.primary[450]} />
  }: { shouldShowLoader?: () => boolean; loader?: ReactElement } = {}
) => (Component: ElementType) => {
  const request = () =>
    Promise.all(castArray(promiseFns).map(promiseFn => promiseFn()));
  return (props: P) => {
    const [receivedFalseStatus, setReceivedFalseStatus] = useState(false);
    const { isLoading, error, reload: currentReload, data } = useAsync({
      promiseFn: request,
      onResolve(response) {
        response.forEach(data => {
          if (data?.status === 0) {
            setReceivedFalseStatus(true);
          } else {
            setReceivedFalseStatus(false);
          }
        });
      }
    });
    const { reload: upperReload } = useContext(PageDataContext);
    const reload = useCallback(() => {
      currentReload();
      if (upperReload) {
        upperReload();
      }
    }, [currentReload, upperReload]);

    let showLoader = shouldShowLoader?.() ?? true;
    if (showLoader) {
      if (isLoading) {
        return loader;
      }
      if (error) {
        return <Loader.Error reload={reload} />;
      }
    }
    if (receivedFalseStatus) return <Loader.Error reload={reload} />;
    return (
      <PageDataContext.Provider value={{ reload }}>
        <Component {...props} data={data} />
      </PageDataContext.Provider>
    );
  };
};

export const withSuspense = (Component: ElementType, loader = <Loader />) => (
  props: AnyObject
) => (
  <ErrorBoundary>
    <Suspense fallback={loader}>
      <Component {...props} />
    </Suspense>
  </ErrorBoundary>
);

export const withGlobalLoader = (Component: ElementType) => (
  props: AnyObject
) => {
  const [showLoader, setShowLoader] = useState(false);
  const show = useCallback(() => {
    setShowLoader(true);
  }, []);
  const hide = useCallback(() => {
    setShowLoader(false);
  }, []);
  const windowHeight = useWindowHeight();
  return (
    <GlobalLoaderContext.Provider
      value={{
        show,
        hide,
        visible: showLoader
      }}
    >
      {showLoader && (
        <div
          style={{
            zIndex: 1000,
            height: `${windowHeight}px`
          }}
          className="absolute w-full opacity-75 bg-black flex justify-center items-center"
        >
          <Loader color={colors.white} showMessage />
        </div>
      )}
      <Component {...props} />
    </GlobalLoaderContext.Provider>
  );
};
